import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import undoDux from 'src/redux/undo';
import uiDux from 'src/redux/ui';
import appDux from 'src/redux/app';
import trackingDux from 'src/redux/tracking';
import constants from 'src/constants';
import trackError from 'src/lib/rollbar';
import createDebug from 'debug';
import EventNames from 'src/lib/eventNames';

const debug = createDebug('designPro.undo.sagas');
const logSagaError = createDebug('designPro.saga.error');

const { UNDO_REDO_OPERATION_FAILED } = constants.errors;

const { handleAttributeSelectionClick, handleClearPaintClick } = uiDux.actions;

const {
  DESIGN_FEATURE_CLICK_UNDO,
  DESIGN_FEATURE_CLICK_REDO,
} = EventNames.viewer;

const config = {
  undo: {
    nextEntry: undoDux.selectors.nextUndoAction,
    clearEntry: undoDux.actions.removeUndoEntry,
  },
  redo: {
    nextEntry: undoDux.selectors.nextRedoAction,
    clearEntry: undoDux.actions.removeRedoEntry,
  },
};

// this is triggered by confirmDeleteSplit saga
// when a split is deleted, this removes references to the removed
// node from the undo/redo stacks. otherwise, app will crash from
// trying to update a delete node
export function* removeDeletedSplitEntriesSaga(action) {
  const { uuids: deletedUUIDs } = action.payload;
  const origUndoStack = yield select(undoDux.selectors.getUndoStack);
  const origRedoStack = yield select(undoDux.selectors.getRedoStack);

  const [undoStack, redoStack] = [origUndoStack, origRedoStack].map((stack) =>
    stack
      // scrub deleted nodes from the undo/redo entries
      .map((entry) => {
        const { data } = entry;
        const { currentStyle, newStyle, ...rest } = data;
        return {
          ...entry,
          data: {
            ...rest,
            currentStyle: {
              ...currentStyle,
              nodes: currentStyle.nodes.filter(
                (uuid) => !deletedUUIDs.includes(uuid),
              ),
            },
            newStyle: {
              ...newStyle,
              nodes: newStyle.nodes.filter(
                (uuid) => !deletedUUIDs.includes(uuid),
              ),
            },
          },
        };
      })
      // filter out any entries that now have empty nodes array
      .filter(
        ({ data }) =>
          !!data.currentStyle.nodes.length && !!data.newStyle.nodes.length,
      ),
  );

  yield put(undoDux.actions.setUndoRedoStacks({ undoStack, redoStack }));
}

export function* undoAttributeSelectionClickSaga(
  { data, originalType },
  isUndo,
) {
  const { currentStyle, newStyle, currentAttribute, newAttribute } = data;
  const { nodes, ...styleAttributes } = currentStyle;
  // apply the current style from when the action was recorded
  yield put(appDux.actions.replaceNodeStyles(nodes, styleAttributes));
  // record the "undo" entry for undoing this attribute selection
  // swip-swop the current and new to re-revert back
  // to what is currently now the situation
  yield put(
    undoDux.actions.recordUndoableAction({
      originalType,
      isUndo,
      data: {
        currentStyle: newStyle,
        currentAttribute: newAttribute,
        newStyle: currentStyle,
        newAttribute: currentAttribute,
      },
    }),
  );

  // if extended sidebar is showing selections for some of the nodes
  // affected by the undo, restore the active editing style and attribute
  // back to what they were so the choice being undone can be remade
  const activeStyle = yield select(appDux.selectors.getActiveEditingStyle);
  const { nodes: activeNodes = [] } = activeStyle;
  const isActiveNodeAffected = activeNodes.some((uuid) => nodes.includes(uuid));
  if (isActiveNodeAffected) {
    yield put(
      appDux.actions.setActiveEditingStyle(currentStyle, currentAttribute),
    );
  }
}

const actionToSagaMap = {
  [handleAttributeSelectionClick]: undoAttributeSelectionClickSaga,
  [handleClearPaintClick]: undoAttributeSelectionClickSaga,
};

export function* handleUndoRedoClickSaga({ type }) {
  try {
    const isUndo = type === undoDux.actions.handleUndoClick.toString();
    const configKey = isUndo ? 'undo' : 'redo';
    const undoTrackingConst = isUndo
      ? DESIGN_FEATURE_CLICK_UNDO
      : DESIGN_FEATURE_CLICK_REDO;
    const operation = yield select(config[configKey].nextEntry);
    const originalAction = { originalAction: operation.originalType };
    const saga = actionToSagaMap[operation.originalType];
    debug(`${configKey} button clicked. operation: `, operation);

    yield put(config[configKey].clearEntry());
    yield put(undoDux.actions.setIsOperationInProgress(true));
    yield call(saga, operation, isUndo);
    yield put(undoDux.actions.setIsOperationInProgress(false));
    yield put(trackingDux.actions.track(undoTrackingConst, originalAction));
  } catch (e) {
    logSagaError(e);
    yield put(appDux.actions.activateErrorModal());
    trackError(UNDO_REDO_OPERATION_FAILED, e);
  }
}

export function* recordUndoableActionSaga({
  payload: { isUndo, originalType, data },
}) {
  const { addRedoEntry, addUndoEntry } = undoDux.actions;
  const addAction = isUndo ? addRedoEntry : addUndoEntry;
  yield put(addAction({ originalType, data }));
  if (typeof isUndo === 'undefined') {
    yield put(undoDux.actions.clearRedoStack());
  }
}

export default function* sagas() {
  yield all([
    takeLatest(
      [undoDux.actions.handleUndoClick, undoDux.actions.handleRedoClick],
      handleUndoRedoClickSaga,
    ),
    takeLatest(undoDux.actions.recordUndoableAction, recordUndoableActionSaga),
    takeLatest(
      undoDux.actions.removeDeletedSplitEntries,
      removeDeletedSplitEntriesSaga,
    ),
  ]);
}
